import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-file-drop-zone',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    <input type="file" #fileInput style="display: none;" [accept]="accept" (change)="onInputChange($event)">

    <div class="drop-zone"
         [class.drag-over]="isDragOver"
         [class.has-file]="!!selectedFile"
         (dragover)="onDragOver($event)"
         (dragleave)="onDragLeave($event)"
         (drop)="onDrop($event)"
         (click)="!selectedFile && fileInput.click()">

      @if (selectedFile) {
        <div class="file-selected-state">
          <mat-icon class="file-icon">description</mat-icon>
          <p class="file-name">{{ selectedFile.name }}</p>
          <p class="file-size">{{ (selectedFile.size / 1024).toFixed(1) }} KB</p>
          <button mat-stroked-button type="button" (click)="clear(); $event.stopPropagation()">
            <mat-icon>close</mat-icon>
            Remove
          </button>
        </div>
      } @else {
        <div class="empty-state">
          <mat-icon class="upload-icon">cloud_upload</mat-icon>
          <p class="drop-text">Drag &amp; drop file here</p>
          <span class="or-text">or</span>
          <button mat-stroked-button color="primary" type="button" (click)="fileInput.click(); $event.stopPropagation()">
            <mat-icon>folder_open</mat-icon>
            Browse Files
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .drop-zone {
      border: 2px dashed #ccc;
      border-radius: 12px;
      padding: 32px;
      text-align: center;
      transition: all 0.2s ease;
      background-color: #fafafa;
      cursor: pointer;
      min-height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .drop-zone:hover { border-color: #1976d2; background-color: #e3f2fd; }
    .drop-zone.drag-over { border-color: #1976d2; background-color: #e3f2fd; border-style: solid; }
    .drop-zone.has-file { border-color: #4caf50; background-color: #e8f5e9; border-style: solid; cursor: default; }

    .empty-state, .file-selected-state {
      display: flex; flex-direction: column; align-items: center; gap: 8px; width: 100%;
    }
    .upload-icon { font-size: 48px; width: 48px; height: 48px; color: #1976d2; }
    .file-icon { font-size: 48px; width: 48px; height: 48px; color: #4caf50; }
    .drop-text { margin: 0; font-size: 16px; font-weight: 500; color: #333; }
    .or-text { color: #888; font-size: 14px; }
    .file-name { margin: 0; font-size: 14px; font-weight: 500; color: #333; word-break: break-all; max-width: 100%; }
    .file-size { margin: 0; font-size: 12px; color: #888; }
  `]
})
export class FileDropZoneComponent {
  @Input() accept = '';
  @Output() fileSelected = new EventEmitter<File>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  isDragOver = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.pick(files[0]);
    }
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.pick(input.files[0]);
    }
  }

  clear(): void {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private pick(file: File): void {
    this.selectedFile = file;
    this.fileSelected.emit(file);
  }
}
